import { useEffect, useState } from 'react';

import { currentUserApi } from '../axios';
import { useUserDataStore } from '../newStore/userDataStore';

/**
 * Custom hook to get the current user's username
 *
 * This hook first checks the user data store for the username to avoid
 * unnecessary API calls. If the username is not in the store (common for
 * public routes), it fetches the username from the API.
 *
 * @returns The current user's username or null if not logged in
 */
export const useCurrentUserName = (): string | null => {
  const [userName, setUserName] = useState<string | null>(null);
  const currentUserFromStore = useUserDataStore((state) => state.currentUser);

  useEffect(() => {
    // Use user from store if available (avoids duplicate API call)
    if (currentUserFromStore?.username) {
      setUserName(currentUserFromStore.username);
      return;
    }

    // Only fetch if store doesn't have user data (for public routes)
    const getUser = async () => {
      try {
        const { username } = await currentUserApi.getCurrentUserName();
        setUserName(username);
      } catch (error) {
        // 401 (Unauthorized) is expected for public routes - user is simply not logged in
        const isAxiosError = error && typeof error === 'object' && 'response' in error;
        const status = isAxiosError ? (error as { response?: { status?: number } }).response?.status : undefined;

        if (status === 401) {
          // Expected: user is not logged in, which is fine for public routes
          setUserName(null);
          return;
        }

        // Other errors (network, 500, etc.) should be logged as they indicate real problems
        console.error('Unexpected error getting current user name:', error);
      }
    };

    getUser();
  }, [currentUserFromStore?.username]);

  return userName;
};
